import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Input, Button } from '@react-apps/design-system';
import { useChangeEmail } from '../hooks/use-change-email';

interface ChangeEmailModalProps {
  open: boolean;
  onClose: () => void;
  /** E-mail atual — mostrado na confirmação de "enviamos o link para …". */
  currentEmail: string;
  /** Chamado após iniciar a troca com sucesso (ex.: recarregar a pendência). */
  onStarted?: () => void;
}

/**
 * Ato 1 da troca de e-mail: reautentica com a senha atual e envia um link ao
 * **e-mail atual**. O e-mail novo é pedido na página pública aberta por esse link.
 */
export function ChangeEmailModal({
  open,
  onClose,
  currentEmail,
  onStarted,
}: ChangeEmailModalProps) {
  const { start, isLoading, error, success } = useChangeEmail();
  const [currentPassword, setCurrentPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);

  const handleClose = () => {
    setCurrentPassword('');
    setNeedsPassword(false);
    onClose();
  };

  const handleSubmit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    const result = await start(currentPassword);
    setNeedsPassword(result.needsPassword);
    if (result.ok) onStarted?.();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={success ? 'Verifique seu e-mail' : 'Alterar e-mail'}
      size="sm"
    >
      {success ? (
        <div className="space-y-4">
          <p className="text-(--color-text-secondary) text-sm">
            Enviamos um link para <strong>{currentEmail}</strong>. Abra o e-mail
            e clique no link para informar o novo endereço.
          </p>
          <Button type="button" onClick={handleClose} className="w-full">
            Entendi
          </Button>
        </div>
      ) : (
        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
          <p className="text-(--color-text-secondary) text-sm">
            Para sua segurança, confirme sua senha atual. Enviaremos um link
            para o seu e-mail atual (<strong>{currentEmail}</strong>) para
            continuar.
          </p>

          <Input
            id="currentPassword"
            label="Senha atual"
            type="password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="••••••••"
          />

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
              {error}
              {needsPassword && (
                <>
                  {' '}
                  <Link to="/forgot-password" className="font-medium underline">
                    Cadastrar senha
                  </Link>
                </>
              )}
            </div>
          )}

          <p className="text-(--color-text-secondary) text-xs">
            Ao concluir a troca, os logins por Google ou Microsoft (se houver)
            deixam de funcionar e todas as suas sessões são encerradas.
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading || !currentPassword}>
              {isLoading ? 'Enviando…' : 'Enviar link'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
